import { blockingIssues, checkRecipe, domainError, err, } from '../domain/index.js';
import { isId, isInstant, isObject, refuse } from './decode.js';
import { decodeFormula, decodeProcess } from './documents.js';
import { saveRevision } from './recipes.js';
/**
 * The first free generated name `<base> <n>` (drafts.md): the smallest n ≥ 1 that no recipe or
 * draft name uses.
 */
export const nextDraftName = (base, taken) => {
    const used = new Set(taken.map((name) => name.trim()));
    let n = 1;
    while (used.has(`${base} ${n}`))
        n++;
    return `${base} ${n}`;
};
/**
 * Saves a draft as a recipe (`blank`, `template`: a new recipe with `newRecipeId`) or as a version
 * of its recipe (`revision`), and deletes the draft in the same transaction (drafts.md). Refused
 * while the recipe has a blocking issue. A draft of a version whose recipe was deleted is saved
 * as a new recipe with `asNewRecipe` (persistence.md, Recipe trash); as a version it is refused
 * (`NotFound`).
 */
export const saveDraft = async (repository, draft, options) => {
    const blocking = blockingIssues(checkRecipe(draft.formula, draft.process));
    if (blocking.length > 0) {
        return err(domainError('RecipeHasBlockingIssues', 'The recipe has blocking issues', {
            issues: blocking,
        }));
    }
    const target = draft.origin.kind === 'revision' && options.asNewRecipe !== true
        ? { recipeId: draft.origin.recipeId, expectedHead: draft.origin.baseVersion }
        : { recipeId: options.newRecipeId, expectedHead: 0 };
    return saveRevision(repository, {
        ...target,
        name: draft.name,
        formula: draft.formula,
        process: draft.process,
        createdAt: options.createdAt,
        fromDraft: draft.id,
    });
};
/** Decodes a draft from untrusted JSON (backups): structure only, never the recipe checks. */
export const decodeDraft = (value, index) => {
    const where = `draft ${index + 1}`;
    if (!isObject(value) || !isId(value.id) || typeof value.name !== 'string') {
        return refuse(`${where} has no valid id or name`);
    }
    if (!isInstant(value.createdAt) || !isInstant(value.updatedAt)) {
        return refuse(`${where} has invalid times`);
    }
    const origin = value.origin;
    if (!isObject(origin))
        return refuse(`${where} has no origin`);
    const valid = origin.kind === 'blank' ||
        (origin.kind === 'template' &&
            isObject(origin.template) &&
            typeof origin.template.id === 'string' &&
            typeof origin.template.version === 'string') ||
        (origin.kind === 'revision' &&
            isId(origin.recipeId) &&
            typeof origin.baseVersion === 'number' &&
            Number.isInteger(origin.baseVersion) &&
            origin.baseVersion >= 1);
    if (!valid)
        return refuse(`${where} has an invalid origin`);
    return {
        id: value.id,
        name: value.name,
        origin: origin,
        formula: decodeFormula(value.formula, `${where} formula`),
        process: decodeProcess(value.process, `${where} process`),
        createdAt: value.createdAt,
        updatedAt: value.updatedAt,
    };
};
//# sourceMappingURL=drafts.js.map